import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Video, Upload, X, Play, Pause, Loader2, AlertCircle, 
  Check, Film, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Video constraints: 30 seconds max, 50MB max
const MAX_VIDEO_DURATION = 30; // seconds
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB in bytes
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];

/**
 * VideoUploader - Upload videos for marketplace listings
 * Max: 30 seconds, 50MB
 */
export default function VideoUploader({ 
  onUpload, 
  existingVideo = null,
  onRemove,
  className = '' 
}) {
  const { getAuthHeaders } = useAuth();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  
  const [video, setVideo] = useState(existingVideo);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setError('');
    
    // Validate file type
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      setError('Invalid video format. Please use MP4, WebM, or MOV.');
      return;
    }
    
    // Validate file size
    if (file.size > MAX_VIDEO_SIZE) {
      setError(`Video must be under 50MB. Your file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`);
      return;
    }
    
    // Validate duration (need to load video first)
    const videoDuration = await getVideoDuration(file);
    if (videoDuration > MAX_VIDEO_DURATION) {
      setError(`Video must be 30 seconds or less. Your video is ${Math.ceil(videoDuration)} seconds.`);
      return;
    }
    
    setDuration(videoDuration);
    
    // Upload the video
    await uploadVideo(file);
  };
  
  const getVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      
      video.onerror = () => {
        reject(new Error('Failed to load video'));
      };
      
      video.src = URL.createObjectURL(file);
    });
  };
  
  const uploadVideo = async (file) => {
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'video');
      
      const response = await axios.post(`${API}/upload/video`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      const videoUrl = response.data.url;
      setVideo(videoUrl);
      
      if (onUpload) {
        onUpload(videoUrl);
      }
      
      toast.success('Video uploaded successfully!');
    } catch (err) {
      console.error('Video upload failed:', err);
      setError(err.response?.data?.detail || 'Failed to upload video');
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
    }
  };
  
  const handleRemove = () => {
    setVideo(null);
    setDuration(0);
    if (onRemove) onRemove();
  };
  
  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <div className={`space-y-3 ${className}`} data-testid="video-uploader">
      {/* Upload Area */}
      {!video && !uploading && (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Film className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Add a product video
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            MP4, WebM, or MOV • Max 30 seconds • Max 50MB
          </p>
        </div>
      )}
      
      {/* Uploading Progress */}
      {uploading && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              <span className="font-medium">Uploading video...</span>
            </div>
            <Progress value={uploadProgress} className="h-2" />
            <p className="text-xs text-gray-500 mt-2 text-right">{uploadProgress}%</p>
          </CardContent>
        </Card>
      )}
      
      {/* Video Preview */}
      {video && !uploading && (
        <Card className="overflow-hidden">
          <div className="relative bg-black aspect-video">
            <video
              ref={videoRef}
              src={video}
              className="w-full h-full object-contain"
              onEnded={() => setIsPlaying(false)}
              onLoadedMetadata={(e) => setDuration(e.target.duration)}
              playsInline
            />
            
            {/* Play/Pause Overlay */}
            <div 
              className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
              onClick={togglePlay}
            >
              <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center">
                {isPlaying ? (
                  <Pause className="w-6 h-6 text-gray-900" />
                ) : (
                  <Play className="w-6 h-6 text-gray-900 ml-1" />
                )}
              </div>
            </div>
            
            {/* Duration Badge */}
            {duration > 0 && (
              <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatDuration(duration)}
              </div>
            )}
            
            {/* Remove Button */}
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <CardContent className="p-3 bg-green-50 dark:bg-green-900/20">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
              <Check className="w-4 h-4" />
              <span className="text-sm font-medium">Video ready</span>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      
      {/* Guidelines */}
      {!video && !uploading && (
        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
          <p className="flex items-center gap-1">
            <Check className="w-3 h-3 text-green-500" />
            Show the product from multiple angles
          </p>
          <p className="flex items-center gap-1">
            <Check className="w-3 h-3 text-green-500" />
            Demonstrate features or functionality
          </p>
          <p className="flex items-center gap-1">
            <Check className="w-3 h-3 text-green-500" />
            Good lighting helps products sell faster
          </p>
        </div>
      )}
    </div>
  );
}
